import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/*
 * Phones: slide the navbar and the bottom tab bar away while the reader
 * scrolls down, and back on a scroll up (the X / Chrome pattern). The
 * section chips stay, moving up to the top edge. Sets
 * <html data-bars="hidden">; the motion is CSS (index.css, [data-hide-bar]),
 * so React doesn't re-render on scroll.
 *
 * Bars always show: near the top, at the end of the page, while the mobile
 * menu is open, when keyboard focus moves into a bar, and after navigating.
 */

// Long scrolling pages only (not the reader, the Listen player, mind maps or
// forms). '/' is the catalogue (exact match only).
const PATHS = [
  '/',
  '/feed',
  '/search',
  '/letters',
  '/bookmarks',
  '/bahishkrit_bharat',
  '/swaraj-critique',
];
const PHONE = '(max-width: 767px)';
const TOP_ZONE = 80; // px from the top where the bars always show
const TOLERANCE = 12; // px of travel before the direction counts

const setHidden = (hidden) => {
  const root = document.documentElement;
  if (hidden) root.dataset.bars = 'hidden';
  else delete root.dataset.bars;
};

export default function useHideBarsOnScroll() {
  const { pathname } = useLocation();
  const enabled = PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );

  useEffect(() => {
    setHidden(false); // every navigation starts with the bars shown
    if (!enabled) return undefined;

    const phone = window.matchMedia(PHONE);
    let lastY = window.scrollY;
    let travel = 0; // distance moved in the current direction
    let frame = 0;

    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      // Clamp: iOS rubber-banding reports y < 0 or > max.
      const y = Math.min(Math.max(window.scrollY, 0), Math.max(max, 0));
      const dy = y - lastY;
      lastY = y;
      if (
        !phone.matches ||
        document.documentElement.dataset.navMenu === 'open'
      ) {
        setHidden(false);
        return;
      }
      if (y < TOP_ZONE || y >= max - 2) {
        setHidden(false);
        travel = 0;
        return;
      }
      if (dy === 0) return;
      // Reset the count when the direction flips.
      travel = Math.sign(dy) === Math.sign(travel) ? travel + dy : dy;
      if (travel > TOLERANCE) setHidden(true);
      else if (travel < -TOLERANCE) setHidden(false);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    // Tabbing into a hidden bar brings the bars back.
    const onFocus = (e) => {
      if (e.target.closest?.('[data-hide-bar]')) setHidden(false);
    };
    const onPhoneChange = () => setHidden(false);

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('focusin', onFocus);
    phone.addEventListener('change', onPhoneChange);
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('focusin', onFocus);
      phone.removeEventListener('change', onPhoneChange);
      if (frame) cancelAnimationFrame(frame);
      setHidden(false);
    };
  }, [enabled, pathname]);
}
